import { useCallback, useEffect, useRef, useState } from "react";
import { CheckedEnvironment } from "./types";
import checkStatus from "./check-status";
import fetchOrganisations from "./fetch-organisations";
import { checkMobileApps } from "./mobile-apps/check-mobile-apps";
import { CheckedMobileApp } from "./mobile-apps/types";
import {
  MOCK_SCENARIO_CHANGE_EVENT,
  resolveMockScenario,
} from "./mock/mock-mode";

export function useStatusCheck(enabled: boolean) {
  const [data, setData] = useState<CheckedEnvironment[]>([]);
  const [mobileApps, setMobileApps] = useState<CheckedMobileApp[]>([]);
  const [lastCheckedDate, setLastCheckedDate] = useState<Date>();
  const [checkGeneration, setCheckGeneration] = useState(0);
  const checkIdRef = useRef(0);

  const performCheck = useCallback(async () => {
    const checkId = ++checkIdRef.current;

    const scenario = resolveMockScenario();
    if (scenario) {
      if (checkId !== checkIdRef.current) return;
      setData(scenario.data.environments);
      setMobileApps(scenario.data.mobileApps);
      setLastCheckedDate(new Date());
      setCheckGeneration(checkId);
      return;
    }

    const [organisations, apps] = await Promise.all([
      fetchOrganisations().catch(() => [] as string[]),
      checkMobileApps().catch(() => [] as CheckedMobileApp[]),
    ]);

    const environments =
      organisations.length > 0
        ? await Promise.all(organisations.map(checkStatus))
        : [];

    // Ignore stale overlapping checks (e.g. React StrictMode double-invoke)
    if (checkId !== checkIdRef.current) return;

    setData(environments);
    setMobileApps(apps);
    setLastCheckedDate(new Date());
    setCheckGeneration(checkId);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    performCheck();
  }, [enabled, performCheck]);

  useEffect(() => {
    if (!enabled) return;

    const onScenarioChange = () => {
      void performCheck();
    };

    window.addEventListener(MOCK_SCENARIO_CHANGE_EVENT, onScenarioChange);
    return () => {
      window.removeEventListener(MOCK_SCENARIO_CHANGE_EVENT, onScenarioChange);
    };
  }, [enabled, performCheck]);

  return {
    data,
    mobileApps,
    lastCheckedDate,
    checkGeneration,
    performCheck,
  };
}
